import React, { useEffect, useImperativeHandle } from 'react';
import {
  Button,
  ButtonColor,
  ButtonVariant, Cell,
  Header4,
  If,
  BulletList,
  List,
  NewLine,
  SimpleBlock,
  Text,
  TextField,
  TextLink,
  usePopupManager,
} from 'ui-builder';
import StepProgressBar, { Step } from '../../projects/launch/StepProgressBar';
import {
  CopyTextToClipboard,
  GridLayout,
  Popup,
  POPUP_DATA_UPDATED,
  PopupSize,
  TextLinkField
} from 'features/nekst-widgets';
import ReactDOM from 'react-dom';
import {
  DnsEntry,
  DomainVerificationType,
  EmailDomain,
  useEmailDomainsService,
  useEventsPublisher
} from 'nekst-api';

import styles from './EmailDomain.module.scss';

interface ProviderInfo {
  name: string;
  url: string;
}

function ProviderGuidesList() {
  const providerInfoEntries: ProviderInfo[] = [
    {
      name: 'GoDaddy',
      url: 'https://www.godaddy.com/en-ie/help/manage-dns-records-680',
    },
    {
      name: 'DreamHost',
      url: 'https://help.dreamhost.com/hc/en-us/articles/360035516812-Adding-custom-DNS-records',
    },
    {
      name: 'CloudFlare',
      url: 'https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/',
    },
    {
      name: 'HostGator',
      url: 'https://www.hostgator.com/help/article/changing-dns-records',
    },
    {
      name: 'Namecheap',
      url: 'https://www.namecheap.com/support/knowledgebase/article.aspx/434/2237/how-do-i-set-up-host-records-for-a-domain/',
    }
  ];

  return (
    <List getDataFunc={async () => providerInfoEntries}>
      <GridLayout>
        <TextField source="name" label="Provider" />
        <TextLinkField
          source="url"
          label="Link"
        />
      </GridLayout>
    </List>
  );
}

function Step1(
  props: {
    domain: EmailDomain
  }
) {
  return (
    <SimpleBlock>
      <Header4>Step 1: Log in to your domain host</Header4>
      <Text>
        Open a new Tab or Browser Window. <NewLine /><NewLine />
        Go to your domain host website and log into your account.<NewLine /><NewLine />
        <Text italic>
        If you are unsure of your domain provider, you can lookup your account here:
        {' '}
        <TextLink
          href={`http://whois.com/whois/${props.domain.domain}`}
          target="_blank"
        >
          {`http://whois.com/whois/${props.domain.domain}`}
        </TextLink>. Check the Name Servers section in the results to identify the service provider managing your DNS records.
        </Text>
      </Text>
      <NewLine />
      <NewLine />
      <Text>
        The steps for setting up DNS records for your domain vary depending on your DNS provider.
        Below, you'll find links to the documentation for several popular DNS providers. Please note
        that this is not an exhaustive list. If your provider isn’t mentioned, you can likely still
        use it with Nekst.
      </Text>
      <NewLine />
      <NewLine />
      <Text>
        Please note that DNS updates may take up to 24 hours to fully propagate. However, in most
        cases,
        the process is completed within an hour.
      </Text>
      <NewLine />
      <NewLine />
      <ProviderGuidesList />
    </SimpleBlock>
  );
}

function StepStatusField(props: { isVerified: boolean }) {
  return (
    <>
      <Text className={props.isVerified ? styles.verified : styles.pending}>
        Status:
        {' '}
        {props.isVerified ? 'Verified' : 'Pending'}
      </Text>
      <NewLine />
      <NewLine />
    </>
  );
}

function DnsEntriesList(
  props: {
    entries: DnsEntry[],
  }
) {
  const withPriority = props.entries.some((entry) => !!entry.priority);
  if (withPriority) {
    return (
      <List getDataFunc={async () => props.entries}>
        <GridLayout weights={[1, 4, 1, 5]}>
          <TextField source="type" label="Type" />
          <Cell label="Name">
            <TextField source="name" label="Name" />
            {' '}
            <CopyTextToClipboard source="name" />
          </Cell>
          {withPriority && (
            <Cell label="Priority">
              <TextField source="priority" label="Priority" noDataMessage="-" />
              <If conditionFunc={(data) => !!data.priority}>
                <CopyTextToClipboard source="priority" />
              </If>
            </Cell>
          )}
          <Cell label="Value">
            <TextField source="value" label="Value" />
            <CopyTextToClipboard source="value" />
          </Cell>
        </GridLayout>
      </List>
    );
  } else {
    return (
      <List getDataFunc={async () => props.entries}>
        <GridLayout weights={[1, 4, 6]}>
          <TextField source="type" label="Type" />
          <Cell label="Name">
            <TextField source="name" label="Name" />
            {' '}
            <CopyTextToClipboard source="name" />
          </Cell>
          <Cell label="Value">
            <TextField source="value" label="Value" />
            <CopyTextToClipboard source="value" />
          </Cell>
        </GridLayout>
      </List>
    );
  }
}

function Step2(
  props: {
    domain: EmailDomain
  }
) {
  const txtRecordData = props.domain.verifications.find(
    (item) => item.type === DomainVerificationType.SES_TXT,
  );

  return (
    <SimpleBlock>
      <Header4>Step 2: Configure TXT records as follows</Header4>

      {txtRecordData && (
        <>
          <StepStatusField isVerified={txtRecordData.isVerified} />
          <DnsEntriesList entries={txtRecordData.entries} />
        </>
      )}
    </SimpleBlock>
  );
}

function Step3(
  props: {
    domain: EmailDomain,
  },
) {
  const dmarcRecordData = props.domain.verifications.find(
    (item) => item.type === DomainVerificationType.SES_DMARC,
  );

  return (
    <SimpleBlock>
      <Header4>Step 3: Configure DMARC record as follows</Header4>

      {dmarcRecordData && (
        <>
          <StepStatusField isVerified={dmarcRecordData.isVerified} />
          <DnsEntriesList entries={dmarcRecordData.entries} />
        </>
      )}
    </SimpleBlock>
  );
}

function Step4(
  props: {
    domain: EmailDomain
  }
) {
  const dkimRecordData = props.domain.verifications.find(
    (item) => item.type === DomainVerificationType.SES_DKIM,
  );

  return (
    <SimpleBlock>
      <Header4>Step 4: Configure DKIM records as follows</Header4>

      {dkimRecordData && (
        <>
          <StepStatusField isVerified={dkimRecordData.isVerified} />
          <DnsEntriesList entries={dkimRecordData.entries} />
        </>
      )}
    </SimpleBlock>
  );
}

function Step5(
  props: {
    domain: EmailDomain,
  }
) {
  const sendFromDomainRecordData = props.domain.verifications.find(
    (item) => item.type === DomainVerificationType.SES_SEND_FROM_DOMAIN,
  );

  const subdomain = sendFromDomainRecordData?.entries[0].name || '';

  return (
    <SimpleBlock>
      <Header4>Step 5: Configure MAIL-FROM records as follows</Header4>

      {sendFromDomainRecordData && (
        <>
          <StepStatusField isVerified={sendFromDomainRecordData.isVerified} />
          <Text>
            Before proceeding with the setup, please check if there are already existing records for
            your subdomain
            {' '}
            <Text bold>{subdomain}</Text>.
            Follow these instructions carefully:
            <NewLine />
            <NewLine />
            <Text bold>
              1. Check for Existing Records:

            </Text>
            <BulletList
              items={[
                <Text>Look under the "MX" and "TXT" record types in your DNS settings for <Text
                  bold>{subdomain}</Text>.</Text>,
                <Text>If you see records already created (and have different values), <Text bold>do
                  not delete or overwrite
                  them</Text> unless you are sure they are not needed for other services.</Text>,
                <Text>If you have any questions or need assistance, please don’t hesitate to reach
                  out to support for help.</Text>
              ]}
            />
            <Text>
              2. <Text bold>If no conflicting records exist</Text>, add the following new records:
            </Text>
          </Text>
          <NewLine />
          <NewLine />
          <DnsEntriesList entries={sendFromDomainRecordData.entries} />
        </>
      )}
    </SimpleBlock>
  );
}

export enum DomainSetUpStepId {
  INTRO = 1,
  TXT_RECORDS = 2,
  DMARC_RECORD = 3,
  DKIM_RECORDS = 4,
  MAIL_FROM_RECORDS = 5,
}

export interface LaunchProjectStepsProps {
  currentStep: DomainSetUpStepId;
  disabled?: boolean;
}

interface StepsMethods {
  next: () => void,
  prev: () => void,
  hasPrevStep: boolean,
  hasNextStep: boolean,
}

function SubmitButton(
  props: {
    domain: EmailDomain
  }
) {

  const emailDomainsService = useEmailDomainsService();

  const popupManager = usePopupManager();

  return (
    <Button
      text="Submit For Review"
      onClick={async () => {
        await emailDomainsService.submitForReview(props.domain.id);
        popupManager.closePopup();
      }}
      color={ButtonColor.GREEN}
      variant={ButtonVariant.FILLED}
    />
  );
}

function StepButtons(props: {
  onNext: () => void,
  onPrev: () => void,
  hasPrev: boolean,
  hasNext: boolean,
  domain: EmailDomain
}) {
  const portalElement = document.getElementById('walkthrough-buttons-portal');

  const [init, setInit] = React.useState(false);

  useEffect(() => {
    setInit(true);
  }, []);

  // create portal
  if (!portalElement) {
    return null;
  }

  if (init) {
    return ReactDOM.createPortal(
      <>
        {props.hasPrev && (
          <Button
            text="Go Back"
            onClick={() => {
              props.onPrev();
            }}
            color={ButtonColor.GRAY}
            variant={ButtonVariant.FILLED}
          />
        )}
        {props.hasNext && (
          <Button
            text="Next Step"
            onClick={() => {
              props.onNext();
            }}
            color={ButtonColor.BLUE}
            variant={ButtonVariant.FILLED}
          />
        )}
        {!props.hasNext && (
          <SubmitButton domain={props.domain} />
        )}
      </>,
      portalElement
    );
  } else {
    return null;
  }
}

export function DomainSetUpWalkthroughWidget(
  props: {
    stepWidgetRef: React.Ref<StepsMethods>,
    domain: EmailDomain
  }
) {
  const [currentStep, setCurrentStep] = React.useState(DomainSetUpStepId.INTRO);

  const steps: Step[] = [
    {
      id: DomainSetUpStepId.INTRO,
      name: 'Intro',
      onClick: () => setCurrentStep(DomainSetUpStepId.INTRO),
      renderElement: <Step1 domain={props.domain} />,
    },
    {
      id: DomainSetUpStepId.TXT_RECORDS,
      name: 'TXT Records',
      onClick: () => setCurrentStep(DomainSetUpStepId.TXT_RECORDS),
      renderElement: <Step2 domain={props.domain} />,
    },
    {
      id: DomainSetUpStepId.DMARC_RECORD,
      name: 'TXT Records',
      onClick: () => setCurrentStep(DomainSetUpStepId.DMARC_RECORD),
      renderElement: <Step3 domain={props.domain} />,
    },
    {
      id: DomainSetUpStepId.DKIM_RECORDS,
      name: 'DKIM Records',
      onClick: () => setCurrentStep(DomainSetUpStepId.DKIM_RECORDS),
      renderElement: <Step4 domain={props.domain} />,
    },
    {
      id: DomainSetUpStepId.MAIL_FROM_RECORDS,
      name: 'MAIL_FROM Records',
      onClick: () => setCurrentStep(DomainSetUpStepId.MAIL_FROM_RECORDS),
      renderElement: <Step5 domain={props.domain} />,
    },
  ];

  useImperativeHandle(props.stepWidgetRef, () => ({
    next: () => setCurrentStep(currentStep + 1),
    prev: () => setCurrentStep(currentStep - 1),
    hasPrevStep: currentStep > 1,
    hasNextStep: currentStep < steps.length,
  }), [currentStep]);

  const eventsPublisher = useEventsPublisher();

  useEffect(() => {
    eventsPublisher.publish(POPUP_DATA_UPDATED, {});
  }, [currentStep]);

  return (
    <>
      <StepProgressBar
        steps={steps}
        currentStep={currentStep}
        size="small"
      />
      <StepButtons
        onNext={() => setCurrentStep(currentStep + 1)}
        onPrev={() => setCurrentStep(currentStep - 1)}
        hasPrev={currentStep > 1}
        hasNext={currentStep < steps.length}
        domain={props.domain}
      />
    </>
  );
}

function ButtonsPortal() {
  return (
    <div id="walkthrough-buttons-portal" />
  );
}

export function DomainSetUpWalkthroughPopup(
  props: {
    domain: EmailDomain
  }
) {
  const stepsRef = React.useRef<StepsMethods>(null);

  return (
    <Popup
      title={`Domain Setup (${props.domain.domain})`}
      content={<DomainSetUpWalkthroughWidget stepWidgetRef={stepsRef} domain={props.domain} />}
      buttons={(
        <ButtonsPortal />
      )}
      size={PopupSize.WIDE}
    />
  );
}

export function useDomainSetUpWalkthroughPopup() {
  const popupManager = usePopupManager();

  return {
    openPopup: (domain: EmailDomain) => popupManager.openPopup(
      <DomainSetUpWalkthroughPopup domain={domain} />
    ),
  };
}
